import { AnsweringClient } from "../clients/answeringClient";
import { AuthRetry, SessionExpiredError } from "../utils/authRetry";

export interface IAssignment {
  assignmentId: number | null;
  applicationId: number | null;
  description: string | null;
  dueDate: string | null;
  finishedAt: string | null;
  startedAt: string | null;
  title: string;
  instrumentId: number | null;

  cardType?: "internal" | "external_link";
  externalUrl?: string;
  studentSeducId?: string;
  applicationName?: string | null;
}

// const SEDUC_PORTUGUES_INSTRUMENT_ID = 93;
// const SEDUC_MATEMATICA_INSTRUMENT_ID = 94;

const SEDUC_MATEMATICA_INSTRUMENT_ID = 133;
const SEDUC_PORTUGUES_INSTRUMENT_ID = 134;

export interface AssignmentControllerDelegate {
  didGetAssignments(assignemnts: IAssignment[]): void;
  applicationShouldLogout(): void;
  genericError(message?: string): void;
  seducNoAssignment(): void;
  parceirosNoAssignment(): void;
  doutoradoNoAssignment(): void;
  noInstrumentsConfiguredError(message: string): void;
}

export class AssignmentController {
  private now: Date;
  private readonly auth: AuthRetry;

  constructor(
    private delegate: AssignmentControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {
    this.now = new Date();
    this.auth = new AuthRetry(this.answeringClient, () =>
      this.delegate.applicationShouldLogout()
    );
  }

  /**
   * Aciona a atribuição automática para um usuário da sequência global de doutorado.
   */
  public async triggerAutomaticDoutoradoAssignment() {
    const accessToken = localStorage.getItem("AccessToken");
    if (!accessToken) {
      this.delegate.applicationShouldLogout();
      return;
    }

    try {
      await this.answeringClient.assignDoutorado(null, accessToken);
      await this.getAssignments(accessToken);
    } catch (error: any) {
      console.error(
        "Falha ao atribuir instrumento de doutorado automaticamente.",
        error
      );
      if (error?.response?.status === 404) {
        const errorMessage =
          error.response.data.message ||
          "Nenhum instrumento disponível para você no momento.";
        this.delegate.noInstrumentsConfiguredError(errorMessage);
      } else {
        console.error(
          "Falha ao atribuir instrumento de doutorado automaticamente.",
          error
        );
        this.delegate.genericError();
      }
    }
  }

  public async getAssignments(accessToken: string) {
    try {
      // The permissions branch below reads the token that actually served the
      // request, so a refreshed retry is not paired with the stale claim.
      const { response, token } = await this.auth.run(
        accessToken,
        async (token) => ({
          response: await this.answeringClient.getAssignedInstruments(token),
          token,
        })
      );

      const assignments: IAssignment[] = response.data || [];
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      const decodedToken = JSON.parse(jsonPayload);

      if (
        decodedToken.permissions?.includes("Doutorado") &&
        assignments.length === 0
      ) {
        this.delegate.doutoradoNoAssignment();
        return;
      }

      if (assignments.length === 0) {
        if (decodedToken.permissions?.includes("Parceiros")) {
          this.delegate.parceirosNoAssignment();
        }
      }

      if (
        decodedToken.permissions &&
        decodedToken?.permissions.includes("Seduc") &&
        !assignments.some(
          (assignment) =>
            assignment.instrumentId === SEDUC_PORTUGUES_INSTRUMENT_ID ||
            assignment.instrumentId === SEDUC_MATEMATICA_INSTRUMENT_ID
        )
      ) {
        this.delegate.seducNoAssignment();
      }

      const sortedAssignments = this.sortAssignments(assignments);

      this.delegate.didGetAssignments(sortedAssignments);
    } catch (error: any) {
      this.reportError(error);
    }
  }

  /** Swallows SessionExpiredError rather than reporting it; see its declaration. */
  private reportError(error: any): void {
    if (error instanceof SessionExpiredError) {
      return;
    }
    this.delegate.genericError(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message
    );
  }

  private getCategory(assignment: IAssignment): number {
    // Define categorias:
    // 1. Não finalizados e não expirados (ativos)
    // 2. External links
    // 3. Não finalizados E expirados (ou sem data definida)
    // 4. Finalizados
    const dueDate = assignment.dueDate ? new Date(assignment.dueDate) : null;
    const isFinished = !!assignment.finishedAt;
    const isExternal = assignment.cardType === "external_link";
    const isLate = dueDate ? dueDate < this.now : false;

    if (isExternal) return 2;
    if (!isFinished) {
      if (dueDate && !isLate) return 1;
      return 3;
    }
    return 4;
  }

  private compareAssignments(a1: IAssignment, a2: IAssignment): number {
    const category1 = this.getCategory(a1);
    const category2 = this.getCategory(a2);

    if (category1 !== category2) {
      return category1 - category2;
    }

    switch (category1) {
      case 1:
      case 3:
        const d1 = a1.dueDate ? new Date(a1.dueDate).getTime() : Infinity;
        const d2 = a2.dueDate ? new Date(a2.dueDate).getTime() : Infinity;
        return d1 - d2;
      case 2:
        return a1.title.localeCompare(a2.title);
      case 4:
        const f1 = a1.finishedAt
          ? new Date(a1.finishedAt).getTime()
          : -Infinity;
        const f2 = a2.finishedAt
          ? new Date(a2.finishedAt).getTime()
          : -Infinity;
        return f2 - f1;
      default:
        return 0;
    }
  }

  private sortAssignments(assignments: IAssignment[]): IAssignment[] {
    this.now = new Date();
    return [...assignments].sort((a1, a2) => this.compareAssignments(a1, a2));
  }
}
